import { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    Button,
    IconButton,
    Typography,
    AppBar,
    Stack,
    Box,
    TextField,
    Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
    CustomTitle,
    handleSessionOut,
    Loader,
} from "../HelperFunctions/HelperFunctions";
import { enqueueSnackbar } from "notistack";
import { UpdateSchedule } from "../API/backend";
import { TimePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { useLoaderData } from "react-router-dom";

const MAIL_FREQUENCIES = ["daily", "weekly", "monthly"];

const WEEKLY_DAYS = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
];

const MONTHLY_DAYS = [
    "start_of_the_month",
    "mid_of_the_month",
    "end_of_the_month",
];

const TIME_ZONES = ["EST", "CST", "MST", "PST", "IST"];

const DAY_LABELS = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    start_of_the_month: "Start of the Month",
    mid_of_the_month: "Mid of the Month",
    end_of_the_month: "End of the Month",
};

function dayOptionsForFrequency(mailFrequency) {
    if (mailFrequency === "weekly") return WEEKLY_DAYS;
    if (mailFrequency === "monthly") return MONTHLY_DAYS;
    return [];
}

function formatDayLabel(value) {
    if (!value) return "";
    return DAY_LABELS[value] || CustomTitle(value);
}

function normalizeFrequency(raw) {
    const value = String(raw || "daily").trim().toLowerCase();
    return MAIL_FREQUENCIES.includes(value) ? value : "daily";
}

function normalizeDay(mailFrequency, rawDay) {
    const allowed = dayOptionsForFrequency(mailFrequency);
    if (!allowed.length) return "";
    const day = String(rawDay || "").trim().toLowerCase().replace(/\s+/g, "_");
    if (allowed.includes(day)) return day;
    // Accept display labels saved previously
    const fromLabel = Object.entries(DAY_LABELS).find(
        ([, label]) => label.toLowerCase() === String(rawDay || "").trim().toLowerCase(),
    );
    if (fromLabel && allowed.includes(fromLabel[0])) return fromLabel[0];
    return allowed[0];
}

const UpdateScheduleDialog = ({
    open,
    onClose,
    reportData,
    userName,
    setRefresh,
}) => {
    const { UID } = useLoaderData();

    const [selectedValues, setSelectedValues] = useState(() => {
        const mail_frequency = normalizeFrequency(
            reportData?.schedule?.mail_frequency,
        );
        return {
            mail_frequency,
            day: normalizeDay(mail_frequency, reportData?.schedule?.day),
            time: reportData?.schedule?.time ?? "",
            time_zone: reportData?.schedule?.time_zone ?? "",
        };
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        const mail_frequency = normalizeFrequency(
            reportData?.schedule?.mail_frequency,
        );
        setSelectedValues({
            mail_frequency,
            day: normalizeDay(mail_frequency, reportData?.schedule?.day),
            time: reportData?.schedule?.time ?? "",
            time_zone: reportData?.schedule?.time_zone ?? "",
        });
    }, [open, reportData]);

    const dayOptions = useMemo(
        () => dayOptionsForFrequency(selectedValues.mail_frequency),
        [selectedValues.mail_frequency],
    );

    const handleFieldChange = (heading, value) => {
        setSelectedValues((prev) => {
            if (heading === "mail_frequency") {
                const mail_frequency = normalizeFrequency(value);
                return {
                    ...prev,
                    mail_frequency,
                    day: normalizeDay(mail_frequency, ""),
                };
            }
            return { ...prev, [heading]: value };
        });
    };

    const handleSave = async () => {
        setLoading(true);
        const mail_frequency = normalizeFrequency(
            selectedValues.mail_frequency,
        );
        const day =
            mail_frequency === "daily"
                ? ""
                : normalizeDay(mail_frequency, selectedValues.day);

        if (!mail_frequency || !selectedValues.time || !selectedValues.time_zone) {
            enqueueSnackbar("Please select all fields", { variant: "info" });
            setLoading(false);
            return;
        }
        if (mail_frequency !== "daily" && !day) {
            enqueueSnackbar("Please select a day for the schedule", {
                variant: "info",
            });
            setLoading(false);
            return;
        }

        try {
            const { schedule_id } = reportData;
            const data = {
                user_id: UID,
                schedule_id,
                user_name: userName,
                schedule: {
                    mail_frequency,
                    day,
                    time: selectedValues.time,
                    time_zone: selectedValues.time_zone,
                },
            };
            const reportUpdateSchedule = await UpdateSchedule(data);
            enqueueSnackbar(`${reportUpdateSchedule.message}`, {
                variant: "success",
            });
            setRefresh(true);
            onClose();
        } catch (error) {
            if (error.status === 401) {
                await handleSessionOut();
            }
            enqueueSnackbar("Error while update schedule", { variant: "error" });
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleTimeChange = (time) => {
        const formattedTime = time
            ? time.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
              })
            : "";
        setSelectedValues((prev) => ({ ...prev, time: formattedTime }));
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return null;
        const [hours, minutes] = timeStr.split(":");
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);
        date.setSeconds(0);
        return date;
    };

    const frequencyOptions = [
        {
            heading: "mail_frequency",
            label: MAIL_FREQUENCIES,
        },
        {
            heading: "day",
            label: dayOptions,
            hide: selectedValues.mail_frequency === "daily",
        },
        {
            heading: "time",
            isTime: true,
        },
        {
            heading: "time_zone",
            label: TIME_ZONES,
        },
    ];

    return (
        <Dialog
            open={open}
            sx={{
                "& .MuiDialog-paper": {
                    minWidth: "35%",
                    minHeight: "40%",
                    top: 10,
                    position: "fixed",
                },
            }}
            PaperProps={{
                elevation: 0,
                sx: { border: "1px solid rgba(0, 0, 0, 0.12)" },
            }}
        >
            <AppBar
                sx={{ position: "relative", bgcolor: "#d7d7d7", height: 45 }}
                elevation={0}
            >
                <Box sx={{ height: 45, alignContent: "center", padding: "0 8px" }}>
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                    >
                        <Stack direction={"row"}>
                            <IconButton
                                size="small"
                                edge="start"
                                color="inherit"
                                onClick={onClose}
                                aria-label="close"
                                style={{ backgroundColor: "#686868" }}
                                disableRipple
                            >
                                <CloseIcon sx={{ fontSize: "15px" }} />
                            </IconButton>
                            <Typography
                                sx={{
                                    ml: 2,
                                    flexGrow: 10,
                                    color: "#1f1f1f",
                                }}
                                component="div"
                                fontWeight={600}
                                fontSize={"16px"}
                            >
                                Schedule
                            </Typography>
                        </Stack>
                        <Stack direction={"row"} gap={1}>
                            <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                disableRipple
                                sx={{
                                    textTransform: "none",
                                    backgroundColor: "#b5dcf2",
                                    color: "#000",
                                    fontWeight: 400,
                                }}
                                onClick={handleSave}
                            >
                                Save
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </AppBar>

            <DialogContent>
                <Stack p={2} gap={2} width={"450px"}>
                    {frequencyOptions.map((option, index) => {
                        if (option.hide) return null;

                        return (
                            <Stack
                                key={index}
                                direction="row"
                                gap={15}
                                justifyContent="space-between"
                            >
                                <Typography sx={{ fontSize: "13px" }}>
                                    {CustomTitle(option.heading)}
                                </Typography>
                                {option.isTime ? (
                                    <TimePicker
                                        format="HH:mm"
                                        size="sm"
                                        style={{ width: 170 }}
                                        value={formatTime(selectedValues.time)}
                                        onChange={handleTimeChange}
                                        ranges={[]}
                                        menuStyle={{
                                            zIndex: 1300,
                                            overflow: "auto",
                                        }}
                                        hideMinutes={(minute) => minute % 10 !== 0}
                                        editable={false}
                                    />
                                ) : (
                                    <Autocomplete
                                        size="small"
                                        options={option.label}
                                        value={selectedValues[option.heading] || null}
                                        onChange={(_, newValue) =>
                                            handleFieldChange(
                                                option.heading,
                                                newValue,
                                            )
                                        }
                                        getOptionLabel={(label) =>
                                            option.heading === "day"
                                                ? formatDayLabel(label)
                                                : CustomTitle(label)
                                        }
                                        isOptionEqualToValue={(opt, val) =>
                                            opt === val
                                        }
                                        disableClearable
                                        componentsProps={{
                                            paper: {
                                                sx: { fontSize: "13px" },
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                name={option.heading}
                                                variant="outlined"
                                                style={{ width: "170px" }}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    style: {
                                                        height: "25px",
                                                        fontSize: "11px",
                                                        padding: "0px 8px",
                                                    },
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true,
                                                    style: {
                                                        fontSize: "11px",
                                                        padding: 0,
                                                        height: "25px",
                                                        lineHeight: "25px",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            </Stack>
                        );
                    })}
                </Stack>
                <Loader loading={loading} />
            </DialogContent>
        </Dialog>
    );
};

export default UpdateScheduleDialog;
